import React, { FunctionComponent } from 'react';
import styles from './TotalsProgressBar.scss';
import classNames from 'classnames';

import { TotalsProgressBarFragment } from '../../../../types/__generated__';
import usePageQuery, { buildQuery } from '../../../hooks/usePageQuery';
import parseCurrency from '../../../utils/parseCurrency';
import Fragment from './TotalsProgressBar.gql';
import TotalsSkeleton from '../../Totals/TotalsSkeleton';
import { Trans } from 'react-i18next';
import raisedPercentage from '../../../utils/raisedPercentage';

const FRAGMENT = buildQuery(Fragment);

export const TotalsProgressBar: FunctionComponent = () => {
  const { loading, error, refetch, data = null } = usePageQuery<
    TotalsProgressBarFragment
  >(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (loading) return <TotalsSkeleton animated displayRefreshButton={false} />;

  if (
    error &&
    !data?.donationSummary?.totalAmount &&
    !data?.targetWithCurrency?.value
  ) {
    return <TotalsSkeleton onRefresh={refetch} />;
  }

  if (!data) return null;

  const { targetWithCurrency, donationSummary, donations } = data;

  const donationCurrency = donationSummary?.totalAmount?.currencyCode ?? 'GBP';
  const donationValue = (donationSummary?.totalAmount?.value ?? 0) / 100;
  const targetAmount = targetWithCurrency?.value;
  const targetCurrency = targetWithCurrency?.currencyCode ?? 'GBP';
  const directDonationCount = donations?.totalCount;
  const aggregatedDonationsCount = donationSummary?.aggregatedDonationsCount;
  const donationTotalCount = aggregatedDonationsCount ?? directDonationCount;
  const percentage = Math.floor(
    raisedPercentage(targetAmount / 100, donationValue),
  );

  return (
    <figcaption>
      <div>
        <span
          data-qa="totaliser-total"
          className={classNames(
            'jg-h1',
            'jg-font-weight-bold',
            'jg-text-color-branded',
          )}
        >
          {parseCurrency(donationCurrency, donationValue, {
            roundDown: false,
            wholeNumber: true,
          })}
        </span>
        <span data-qa="totaliser-message" className="jg-text--light">
          <span>
            <Trans i18nKey="common.totalRaised"> total raised</Trans>
            {Boolean(targetAmount) && (
              <Trans i18nKey="page.totaliser_description">
                {' '}
                of{' '}
                <strong className={'jg-text-color-branded'}>
                  {{
                    targetAmount: parseCurrency(
                      targetCurrency,
                      targetAmount / 100,
                      {
                        roundDown: true,
                        wholeNumber: true,
                      },
                    ),
                  }}
                </strong>{' '}
                target
              </Trans>
            )}
          </span>
        </span>
      </div>
      {Boolean(targetAmount) && (
        <div className={styles.progressBarContainer}>
          <div
            className={styles.foregroundBar}
            style={{ width: (percentage > 100 ? 100 : percentage) + '%' }}
          ></div>
        </div>
      )}
      <div className="jg-text--light">
        {(Boolean(donationTotalCount) && (
          <Trans
            i18nKey="page.totaliser_progress_bar_supporters"
            count={donationTotalCount}
          >
            {' '}
            by{' '}
            <span className={classNames('jg-text--light')}>
              {{ donationsCount: donationTotalCount }} donations from supporters
            </span>
          </Trans>
        )) || (
          <Trans i18nKey="page.totaliser_progress_bar_be_the_first"></Trans>
        )}
        {Boolean(targetAmount) && (
          <strong
            className={classNames(
              'jg-h2',
              'jg-font-weight-bold',
              'jg-text-color-branded',
              'jg-pull-right',
            )}
          >
            {percentage}%
          </strong>
        )}
      </div>
    </figcaption>
  );
};
